import * as XLSX from 'xlsx';

const ANSWER_LETTERS = ['A', 'B', 'C', 'D'];

const getTrimmedString = (value) => String(value ?? '').trim();

const getCellValue = (row, keys) => {
    for (const key of keys) {
        if (Object.prototype.hasOwnProperty.call(row, key) && row[key] !== undefined && row[key] !== null) {
            return row[key];
        }
    }
    return '';
};

const getWorksheetCellValue = (cell) => {
    if (!cell) {
        return '';
    }

    // Prefer rich-text HTML generated by SheetJS so inline styles like bold/underline are preserved.
    if (typeof cell.h === 'string' && cell.h.trim() !== '') {
        return cell.h;
    }

    if (cell.w !== undefined && cell.w !== null) {
        return String(cell.w);
    }

    if (cell.v !== undefined && cell.v !== null) {
        return String(cell.v);
    }

    return '';
};

export const sheetToJsonPreserveRichText = (worksheet) => {
    if (!worksheet || !worksheet['!ref']) {
        return [];
    }

    const range = XLSX.utils.decode_range(worksheet['!ref']);
    const headerRowIndex = range.s.r;
    const headers = [];

    for (let col = range.s.c; col <= range.e.c; col += 1) {
        const headerCellAddress = XLSX.utils.encode_cell({ r: headerRowIndex, c: col });
        const headerCell = worksheet[headerCellAddress];
        const headerValue = getTrimmedString(headerCell?.v);
        headers.push(headerValue || `__EMPTY_${col - range.s.c}`);
    }

    const rows = [];

    for (let rowIndex = headerRowIndex + 1; rowIndex <= range.e.r; rowIndex += 1) {
        const row = {};
        let hasAnyValue = false;

        headers.forEach((header, headerIndex) => {
            const col = range.s.c + headerIndex;
            const cellAddress = XLSX.utils.encode_cell({ r: rowIndex, c: col });
            const cell = worksheet[cellAddress];
            const value = getWorksheetCellValue(cell);

            if (value !== '') {
                hasAnyValue = true;
            }

            row[header] = value;
        });

        if (hasAnyValue) {
            rows.push(row);
        }
    }

    return rows;
};

const parseCorrectAnswers = (rawValue) => {
    const value = getTrimmedString(rawValue).toUpperCase();
    if (!value) {
        return [];
    }

    return value
        .split(',')
        .map((answer) => answer.trim())
        .filter((answer) => ANSWER_LETTERS.includes(answer));
};

export const parseQuizRows = (rows) => {
    const questions = [];
    const warnings = [];
    const errors = [];

    rows.forEach((row, index) => {
        const rowNumber = index + 2;
        const question = getTrimmedString(getCellValue(row, ['Câu hỏi', 'Question', 'CauHoi']));

        if (!question) {
            errors.push(`Dòng ${rowNumber}: thiếu cột "Câu hỏi".`);
            return;
        }

        let questionType = getTrimmedString(getCellValue(row, ['Loại câu hỏi', 'Type', 'LoaiCauHoi'])).toLowerCase();

        if (questionType === 'text') {
            const sampleAnswers = getTrimmedString(getCellValue(row, ['Đáp án mẫu', 'Sample Answer', 'DapAnMau']));
            if (!sampleAnswers) {
                errors.push(`Dòng ${rowNumber}: câu hỏi text cần có "Đáp án mẫu".`);
                return;
            }

            const acceptableAnswers = sampleAnswers
                .split(';')
                .map((answer) => answer.trim())
                .filter((answer) => answer !== '')
                .map((answer) => answer.toLowerCase());

            if (acceptableAnswers.length === 0) {
                errors.push(`Dòng ${rowNumber}: "Đáp án mẫu" rỗng hoặc sai định dạng.`);
                return;
            }

            questions.push({
                id: questions.length + 1,
                rowNumber,
                question,
                questionType: 'text',
                options: [],
                correctAnswers: acceptableAnswers,
                explanation: getTrimmedString(getCellValue(row, ['Giải thích', 'Explanation', 'GiaiThich', 'explanation'])),
            });
            return;
        }

        const options = [
            getCellValue(row, ['Đáp án A', 'A', 'DapAnA', 'a']),
            getCellValue(row, ['Đáp án B', 'B', 'DapAnB', 'b']),
            getCellValue(row, ['Đáp án C', 'C', 'DapAnC', 'c']),
            getCellValue(row, ['Đáp án D', 'D', 'DapAnD', 'd']),
        ]
            .map((option) => getTrimmedString(option))
            .filter((option) => option !== '');

        if (options.length < 2) {
            errors.push(`Dòng ${rowNumber}: cần ít nhất 2 đáp án (A/B/C/D).`);
            return;
        }

        let correctAnswers = parseCorrectAnswers(getCellValue(row, ['Đáp án đúng', 'Correct', 'DapAnDung', 'correct']));

        if (correctAnswers.length === 0) {
            correctAnswers = ['A'];
            warnings.push(`Dòng ${rowNumber}: không có "Đáp án đúng" hợp lệ, hệ thống tạm mặc định là A.`);
        }

        const hasOutOfRangeAnswer = correctAnswers.some((letter) => ANSWER_LETTERS.indexOf(letter) >= options.length);
        if (hasOutOfRangeAnswer) {
            errors.push(`Dòng ${rowNumber}: "Đáp án đúng" không khớp số lượng đáp án đang có.`);
            return;
        }

        if (!questionType) {
            if (options.length === 2) {
                questionType = 'boolean';
            } else if (correctAnswers.length > 1) {
                questionType = 'multiple';
            } else {
                questionType = 'single';
            }
            warnings.push(`Dòng ${rowNumber}: thiếu "Loại câu hỏi", đã tự nhận dạng là ${questionType}.`);
        }

        if (!['single', 'multiple', 'boolean'].includes(questionType)) {
            errors.push(`Dòng ${rowNumber}: loại câu hỏi "${questionType}" không hợp lệ.`);
            return;
        }

        questions.push({
            id: questions.length + 1,
            rowNumber,
            question,
            questionType,
            options,
            correctAnswers,
            explanation: getTrimmedString(getCellValue(row, ['Giải thích', 'Explanation', 'GiaiThich', 'explanation'])),
        });
    });

    return {
        questions,
        warnings,
        errors,
        stats: {
            totalRows: rows.length,
            validRows: questions.length,
            invalidRows: rows.length - questions.length,
        },
    };
};

export const parseQuizExcelFromArrayBuffer = (arrayBuffer) => {
    if (!arrayBuffer || arrayBuffer.byteLength === 0) {
        throw new Error('File Excel rỗng hoặc không đọc được dữ liệu.');
    }

    const workbook = XLSX.read(arrayBuffer, { type: 'array', cellHTML: true, cellText: true });
    if (!workbook.SheetNames || workbook.SheetNames.length === 0) {
        throw new Error('File Excel không có sheet nào.');
    }

    const sheetName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[sheetName];
    const rows = sheetToJsonPreserveRichText(worksheet);

    if (!rows || rows.length === 0) {
        throw new Error('Sheet không có dữ liệu.');
    }

    return {
        sheetName,
        ...parseQuizRows(rows),
    };
};
